import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Surface, Button, Badge, Alert } from '../../ui/components';
import { User, Zap, ShieldCheck, FileCheck, MessageCircle, Send, CheckCircle, ArrowRight } from 'lucide-react';
import { setCurrentStep, setUnderwritingChoice } from '../../store/loanApplicationSlice';
import { OwenAssistant } from '../Owen';

const SubmitToUnderwriter = () => {
  const dispatch = useDispatch();
  const { readinessScore, applicationData, recommendedProduct } = useSelector(
    (state) => state.loanApplication
  );
  const [selectedOption, setSelectedOption] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (option) => {
    setSelectedOption(option);
    setIsSubmitting(true);
    dispatch(setUnderwritingChoice(option));

    // Simulate submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitted(true);
    }, 2000);
  };

  const handleContinue = () => {
    if (selectedOption === 'ai') {
      dispatch(setCurrentStep(3.5)); // AI Underwriting Flow
    } else {
      dispatch(setCurrentStep(3.7)); // Human Underwriting Flow
    }
  };

  if (submitted) {
    return (
      <div style={{ padding: 'var(--lk-space-6) 0', textAlign: 'center' }}>
        <Surface padding="var(--lk-space-8)" style={{ maxWidth: 600, margin: '0 auto' }}>
          <CheckCircle size={64} color="var(--lk-success-500)" style={{ marginBottom: 'var(--lk-space-4)' }} />
          <h2 style={{ fontWeight: 700, marginBottom: 'var(--lk-space-3)' }}>
            Application Submitted!
          </h2>
          <p style={{ color: 'var(--lk-text-tertiary)', marginBottom: 'var(--lk-space-5)' }}>
            Your application has been submitted for {selectedOption === 'ai' ? 'AI' : 'human'} underwriting review.
          </p>

          <Alert variant="success" style={{ marginBottom: 'var(--lk-space-5)', textAlign: 'left' }}>
            <strong>What happens next:</strong><br />
            {selectedOption === 'ai'
              ? "Owen AI will analyze your application in minutes and provide a detailed underwriting report."
              : "A certified underwriter will review your application within 24-48 hours."}
          </Alert>

          <Button variant="primary" size="lg" icon={ArrowRight} onClick={handleContinue}>
            Continue to Underwriting
          </Button>
        </Surface>
      </div>
    );
  }

  const FeatureRow = ({ icon: Icon, color, children }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-2)' }}>
      <Icon size={18} color={color} />
      <span style={{ fontSize: 'var(--lk-text-sm)', color: 'var(--lk-text-secondary)' }}>{children}</span>
    </div>
  );

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Owen's Message */}
      <div style={{ marginBottom: 'var(--lk-space-4)' }}>
        <OwenAssistant
          message="Congratulations! You've completed your loan application. Now it's time to submit for underwriting review. This is where your application gets verified and you receive your official Readiness Score."
          tips={[
            `📊 Your current readiness score: ${readinessScore}/100`,
            "🔒 All information is encrypted and secure",
            "⚡ AI underwriting takes minutes, human review takes 24-48 hours",
          ]}
        />
      </div>

      {/* Application Summary */}
      <Surface padding="var(--lk-space-4)" style={{ marginBottom: 'var(--lk-space-4)' }}>
        <h5 style={{ fontWeight: 700, marginBottom: 'var(--lk-space-3)' }}>Application Summary</h5>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--lk-space-3)' }}>
          <div>
            <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)' }}>Loan Product</div>
            <div style={{ fontWeight: 600 }}>{recommendedProduct?.shortName || 'Conventional'}</div>
          </div>
          <div>
            <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)' }}>Loan Amount</div>
            <div style={{ fontWeight: 600 }}>${(applicationData?.loanInfo?.loanAmount || 0).toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)' }}>Property Type</div>
            <div style={{ fontWeight: 600 }}>{applicationData?.propertyInfo?.propertyType || 'Single Family'}</div>
          </div>
          <div>
            <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)' }}>Readiness</div>
            <div style={{ fontWeight: 600, color: 'var(--lk-primary-600)' }}>{readinessScore}/100</div>
          </div>
        </div>
      </Surface>

      {/* Underwriting Options */}
      <h4 style={{ fontWeight: 700, marginBottom: 'var(--lk-space-2)' }}>Choose Your Underwriting Path</h4>
      <p style={{ color: 'var(--lk-text-tertiary)', marginBottom: 'var(--lk-space-4)' }}>Select how you'd like your application reviewed</p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--lk-space-4)' }}>
        {/* AI Underwriting */}
        <Surface
          padding="var(--lk-space-5)"
          onClick={() => setSelectedOption('ai')}
          style={{
            cursor: 'pointer', transition: 'all var(--lk-duration-normal) var(--lk-ease)',
            border: selectedOption === 'ai' ? '2px solid var(--lk-primary-500)' : '1px solid var(--lk-border)',
            boxShadow: selectedOption === 'ai' ? '0 0 0 3px var(--lk-primary-100)' : 'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-3)', marginBottom: 'var(--lk-space-3)' }}>
            <img
              src="/assets/owen-avatar.png"
              alt=""
              style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover' }}
            />
            <div>
              <h5 style={{ fontWeight: 700, marginBottom: 2 }}>Owen AI Underwriting</h5>
              <Badge variant="primary">Recommended</Badge>
            </div>
          </div>

          <p style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)', marginBottom: 'var(--lk-space-3)' }}>
            Get instant analysis powered by AI trained on millions of successful loan files.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-2)', marginBottom: 'var(--lk-space-4)' }}>
            <FeatureRow icon={Zap} color="var(--lk-success-500)">Results in minutes</FeatureRow>
            <FeatureRow icon={ShieldCheck} color="var(--lk-success-500)">99.2% accuracy rate</FeatureRow>
            <FeatureRow icon={FileCheck} color="var(--lk-success-500)">Detailed findings report</FeatureRow>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--lk-border-subtle)', margin: 'var(--lk-space-3) 0' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 'var(--lk-text-xl)', fontWeight: 700, color: 'var(--lk-primary-600)' }}>Free</span>
            <Button
              variant={selectedOption === 'ai' ? 'primary' : 'secondary'}
              icon={Send}
              onClick={(e) => { e.stopPropagation(); handleSubmit('ai'); }}
              disabled={isSubmitting}
            >
              {isSubmitting && selectedOption === 'ai' ? 'Submitting...' : 'Submit to Owen'}
            </Button>
          </div>
        </Surface>

        {/* Human Underwriting */}
        <Surface
          padding="var(--lk-space-5)"
          onClick={() => setSelectedOption('human')}
          style={{
            cursor: 'pointer', transition: 'all var(--lk-duration-normal) var(--lk-ease)',
            border: selectedOption === 'human' ? '2px solid var(--lk-gray-500)' : '1px solid var(--lk-border)',
            boxShadow: selectedOption === 'human' ? '0 0 0 3px var(--lk-gray-100)' : 'none',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--lk-space-3)', marginBottom: 'var(--lk-space-3)' }}>
            <div style={{
              width: 56, height: 56, borderRadius: 'var(--lk-radius-lg)',
              backgroundColor: 'var(--lk-gray-500)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <User size={28} color="white" />
            </div>
            <div>
              <h5 style={{ fontWeight: 700, marginBottom: 2 }}>Human Underwriter</h5>
              <Badge variant="neutral">Premium</Badge>
            </div>
          </div>

          <p style={{ color: 'var(--lk-text-tertiary)', fontSize: 'var(--lk-text-sm)', marginBottom: 'var(--lk-space-3)' }}>
            Work with a certified underwriter for complex situations or personal guidance.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--lk-space-2)', marginBottom: 'var(--lk-space-4)' }}>
            <FeatureRow icon={User} color="var(--lk-gray-500)">Dedicated underwriter assigned</FeatureRow>
            <FeatureRow icon={MessageCircle} color="var(--lk-gray-500)">Direct chat & support</FeatureRow>
            <FeatureRow icon={ShieldCheck} color="var(--lk-gray-500)">Complex scenario expertise</FeatureRow>
          </div>

          <hr style={{ border: 'none', borderTop: '1px solid var(--lk-border-subtle)', margin: 'var(--lk-space-3) 0' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ fontSize: 'var(--lk-text-xl)', fontWeight: 700, color: 'var(--lk-text-primary)' }}>$99</span>
              <div style={{ fontSize: 'var(--lk-text-xs)', color: 'var(--lk-text-quaternary)' }}>Refunded by lender</div>
            </div>
            <Button
              variant={selectedOption === 'human' ? 'primary' : 'secondary'}
              icon={Send}
              onClick={(e) => { e.stopPropagation(); handleSubmit('human'); }}
              disabled={isSubmitting}
            >
              {isSubmitting && selectedOption === 'human' ? 'Submitting...' : 'Submit to Underwriter'}
            </Button>
          </div>
        </Surface>
      </div>

      {/* Progress indicator during submission */}
      {isSubmitting && (
        <Surface padding="var(--lk-space-4)" style={{ marginTop: 'var(--lk-space-4)', textAlign: 'center' }}>
          <p style={{ marginBottom: 'var(--lk-space-2)' }}>Submitting your application...</p>
          <div style={{ height: 6, borderRadius: 3, backgroundColor: 'var(--lk-gray-100)', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: '100%', borderRadius: 3,
              backgroundColor: 'var(--lk-primary-500)',
              animation: 'shimmer 1.5s infinite',
              background: 'linear-gradient(90deg, var(--lk-primary-400) 0%, var(--lk-primary-600) 50%, var(--lk-primary-400) 100%)',
              backgroundSize: '200% 100%',
            }} />
          </div>
        </Surface>
      )}
    </div>
  );
};

export default SubmitToUnderwriter;
